import { state } from '@codesuma/baseline'
import { IDayCell } from './calendar'
import settings from './settings'
import customOccasions from './custom-occasions'
import events from './events'
import { occasionCopyIds } from './occasion-copy-ledger'
import { MIRROR_CALENDARS } from '../configs/calendars'
import { IPhoneEvent, IRawEvent, dayKey, indexByDay, toEvent } from './phone-events'

// The phone's calendar, read into the app.
//
// Read only, and read live: nothing the phone owns is ever copied into Emrouz's
// own storage, and Emrouz writes nothing back. One of the phone's events is
// changed in the phone's own calendar app (see openPhone), so this asks for
// read permission and nothing else.
//
// The plugin is reached through window.Capacitor rather than imported, the way
// the reminders, theme and widget services reach theirs: it belongs to the
// native shell, not to a bundle that also has to run in a browser. On the web
// it is simply absent, available() is false, and nothing above ever asks.
//
// Nothing here is persisted. Other people's appointments are not the app's to
// keep, so the cache lives in memory and every launch starts empty.

const capacitor = () => (window as any).Capacitor
const plugin = () => capacitor()?.Plugins?.Calendar
const isNative = () => !!capacitor()?.isNativePlatform?.()

// What has been fetched, by Gregorian month: the month is the unit because the
// grid asks for a month at a time. The day index is derived from it, so there
// is one place a duplicate can be dropped and one place the sort happens.
const months = new Map<string, IPhoneEvent[]>()
let byDay: Record<string, IPhoneEvent[]> = {}

const monthKey = (gy: number, gm: number) => `${gy}-${gm}`


// A browser has no phone calendar to read, so nothing offers the feature there
// — see the settings sheet and the day page's actions.
const available = () => isNative() && !!plugin()

let granted = false

// Whether an event comes from a calendar the person ticked in settings. By id,
// because two accounts can each hold a calendar with the same name. No list at
// all is an install from before there was a choice, which reads everything.
const picked = (raw: IRawEvent) => {
    const ids = settings.get().phoneCalendars
    return !ids || ids.includes(String(raw.calendarId))
}

// A month's fetch also returns the events that only overlap its edges, so an
// event spanning a boundary arrives in both months — the same occurrence twice
// over, with the same id and the same start, and wanted once.
//
// This is also where an adopted event drops out. Emrouz has taken it over as an
// occasion, which is already drawn on that day, and the phone still holds the
// original — so listing it here would be the same day written twice. Done on
// indexing rather than on fetching, so adopting one takes effect at once
// without going back to the phone for anything.
//
// Returns whether the days came out different. A page told of a change
// rebuilds the calendar under the reader, so a fetch that brought nothing new
// keeps quiet.
function reindex(): boolean {
    const adopted = new Set(customOccasions.sources())
    // Copies older versions of Emrouz wrote into the phone. They were left
    // there as the user's own, but reading one back would draw the same thing
    // twice on its day.
    const published = new Set([...events.publishedIds(), ...occasionCopyIds()])
    const unique = new Map<string, IPhoneEvent>()
    for (const list of months.values()) {
        for (const e of list) {
            if (adopted.has(e.id) || published.has(e.id)) continue
            unique.set(`${e.id}:${e.start}`, e)
        }
    }
    const next = indexByDay([...unique.values()])
    const changed = JSON.stringify(next) !== JSON.stringify(byDay)
    byDay = next
    return changed
}

async function permitted(): Promise<boolean> {
    if (granted) return true
    granted = (await plugin()?.checkPermissions?.())?.readCalendar === 'granted'
    return granted
}

async function fetchMonth(key: string) {
    const [gy, gm] = key.split('-').map(Number)
    // Local midnight to local midnight: the plugin takes epoch milliseconds,
    // and the month on screen is the month where the phone is.
    const startDate = new Date(gy, gm - 1, 1).getTime()
    const endDate = new Date(gy, gm, 1).getTime()
    // Always an explicit range — left to itself the plugin searches from six
    // months back to two years on, which is most of the calendar.
    const res = await plugin().findEvents({ startDate, endDate })
    // Only the calendars ticked in settings. The calendar older versions filled
    // with Emrouz's own occasions is skipped outright: reading it back would
    // draw each of them a second time.
    months.set(key, ((res?.events || []) as IRawEvent[])
        .filter(picked)
        .map(toEvent)
        .filter(e => !MIRROR_CALENDARS.includes(e.calendar)))
}

// The events for every month the given cells touch, fetched once each. Called
// by the month grid as it builds, and again after anything drops the cache.
async function ensure(cells: IDayCell[]) {
    if (!available() || !settings.get().showPhoneEvents) return
    if (!await permitted()) return

    const missing = [...new Set(cells.map(c => monthKey(c.gy, c.gm)))].filter(k => !months.has(k))
    if (!missing.length) return
    // Claimed before the fetch rather than after, so three slides asking for
    // the same month in the same frame fetch it once.
    for (const k of missing) months.set(k, [])

    try {
        await Promise.all(missing.map(fetchMonth))
    } catch {
        // Permission withdrawn from the phone's settings while the app was
        // open, or a calendar the platform would not open. There is nothing to
        // show and nothing worth saying — but a later look should try again,
        // so the claim is given back.
        for (const k of missing) months.delete(k)
        granted = false
        return
    }

    if (reindex()) state.set('phone-events', byDay)
}

// Every month held, read again where it stands. Dropping them all instead
// emptied the days on screen and filled them again a moment later, and each
// of those rebuilt the calendar and the day's sheet.
async function refresh() {
    if (!months.size) return
    try {
        await Promise.all([...months.keys()].map(fetchMonth))
    } catch {
        // As in ensure: nothing to show, and a later look tries again.
        granted = false
        invalidate()
        return
    }
    if (reindex()) state.set('phone-events', byDay)
}

// Synchronous, because the grid and the day slide are built in one pass: they
// read whatever the last fetch left here, and redraw when the next one lands.
//
// The switch is checked here and not only where the cache is filled. Turning it
// off drops the cache too, but the pages hear about the setting before this
// service does — so without this they would draw one last frame of events on
// their way to hearing it.
function forDay(cell: IDayCell): IPhoneEvent[] {
    if (!settings.get().showPhoneEvents) return []
    return byDay[dayKey(cell.gy, cell.gm, cell.gd)] || []
}

// One wide read that goes nowhere near the month cache.
//
// The adoption scan wants a year and a bit in a single sweep, to see whether an
// event comes back on the same day next year. Keeping it out of the cache
// matters: those months were never asked for by anything on screen, and holding
// them would keep a year of other people's appointments in memory for a check
// that runs once a launch.
async function scanRange(from: number, to: number): Promise<IPhoneEvent[]> {
    if (!available() || !await permitted()) return []
    try {
        const res = await plugin().findEvents({ startDate: from, endDate: to })
        const owned = new Set([...events.publishedIds(), ...occasionCopyIds()])
        return ((res?.events || []) as IRawEvent[])
            .filter(picked)
            .map(toEvent)
            .filter(e => !MIRROR_CALENDARS.includes(e.calendar) && !owned.has(e.id))
    } catch {
        return []
    }
}

// The phone's own calendars, for the list in settings. Reading them needs read
// permission, so this is empty until that has been given.
async function calendars(): Promise<{ id: string, name: string }[]> {
    if (!available() || !await permitted()) return []
    try {
        const res = await plugin().listCalendars()
        return (res?.calendars || []).map((c: any) => ({
            id: String(c.id),
            name: String(c.displayName || c.name || ''),
        })).filter((c: { name: string }) => !!c.name)
    } catch {
        return []
    }
}

// Hands the day over to the phone's own calendar app, which is where one of
// its events is changed: Emrouz only reads them. Needs no permission.
function openPhone(at: number) {
    plugin()?.openCalendar?.({ date: at })
}

// Drops everything held and tells the pages, which redraw and ask again.
function invalidate() {
    months.clear()
    byDay = {}
    state.set('phone-events', byDay)
}

export default {
    available,

    // Asked for only when the switch is turned on, so someone who never wants
    // the feature is never prompted.
    async enable(): Promise<boolean> {
        if (!available()) return false
        const status = await plugin().requestPermissions({ permissions: ['readCalendar'] })
        granted = status?.readCalendar === 'granted'
        // A first connection reads nothing until calendars are ticked in
        // settings. A phone can hold tens of them — subscribed, hidden,
        // birthdays — and most are not wanted beside Emrouz's own days.
        if (granted && settings.get().phoneCalendars === null) settings.update({ phoneCalendars: [] })
        return granted
    },

    ensure,
    forDay,
    scanRange,
    calendars,
    openPhone,
    invalidate,


    init() {
        if (!available()) return
        // Events can be added, changed or deleted in the phone's own calendar
        // while Emrouz sits in the background, and there is no change feed to
        // subscribe to — so what was fetched is read again on the way back in.
        capacitor()?.Plugins?.App?.addListener?.('appStateChange',
            ({ isActive }: { isActive: boolean }) => { if (isActive) refresh() })

        // Turned off, the events have to leave the calendar at once; turned on,
        // nothing is held yet; and a calendar ticked or unticked changes which
        // events belong — so any of those starts the cache over.
        const reading = () => {
            const s = settings.get()
            return JSON.stringify([s.showPhoneEvents, s.phoneCalendars])
        }
        let last = reading()
        state.on('settings', () => {
            const next = reading()
            if (next === last) return
            last = next
            invalidate()
        })

        // Adopting one of the phone's events as an occasion — or deleting that
        // occasion again — changes which of them belong on screen, but not what
        // was fetched. So the index is rebuilt where it stands, without going
        // back to the phone for anything.
        state.on('custom-occasions', () => {
            reindex()
            state.set('phone-events', byDay)
        })
        // Emrouz's own events say which old copies to leave out (see
        // reindex), so a change to them is the same rebuild.
        state.on('events', () => {
            reindex()
            state.set('phone-events', byDay)
        })
    },
}
